// useContentLoader.ts
import { useState, useEffect, useCallback } from 'react';
import { cond, equals, includes, always, T, pathOr } from 'ramda';
import SiteSettings from 'src/siteSetting';
import { renderHandlebars } from 'src/utilities/handlebars';
import { getS3Url } from 'src/utilities/logo';
import {
    CARD_HOLDER_AGREEMENT_HBS,
    CONSENT_HBS,
    PRICING_TERMS_HBS,
    PRIVACY_POLICY_HBS,
    TERM_OF_USE_HBS,
    RENASANT_PRIVACY_HBS,
    RENASANT_TERMS_HBS,
    CARD_HOLDER_AGREEMENT,
    CONSENT,
    PRICING_AND_TERMS,
    RENASANT_BANK_PRIVACY_POLICY,
    RENASANT_BANK_TERMS_OF_USE,
    DIGITAL_TERMS_HBS,
    CONSENT_E_STATEMENTS,
    E_STATEMENTS_TERMS_HBS,
    CONSENT_DIGITAL,
    TERM_OF_USE,
    ATTUNE_TERMS_OF_USE,
    PRIVACY_POLICY,
    ATTUNE_PRIVACY_POLICY,
    RSNT_CREDIT_POLICY,
    CREDIT_CONSENT,
    STAR_CREDIT_POLICY,
    STAR_CREDIT_CONSENT,
    COOKIE_POLICY_CONSENT,
} from 'src/constants';
import { COOKIEPOLICY } from 'src/features/constants';

const CONTENT_HBS = 'hbs';
const CONTENT_EXTERNAL_URL = 'external_url';
const CONTENT_OPEN_EXTERNAL_URL = 'open_external_url';

/**
 * Tenant policy available data to show to the user.
 */
export interface TenantPolicyCondition {
  name: string;
  alias?:string
  public_hbs_url?: string;
  iframe_external_url?: string;
  open_external_url?: string;
}

export const useContentLoader = () => {
    const [open, setOpen] = useState(false);
    const [openIframe, setOpenIframe] = useState(false);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [externalURL, setExternalURL] = useState('');

    const handleClose = () => setOpen(false);
    const handleIframeClose = () => setOpenIframe(false);

    const getDefaultHBSOptions = (currentTitle: string) => {
        return cond([
            [
                () => includes(currentTitle, [TERM_OF_USE, ATTUNE_TERMS_OF_USE]),
                always({
                    fileName: TERM_OF_USE_HBS,
                    additionalContent: { pre_text: SiteSettings.generalTermsPre() },
                }),
            ],
            [
                () => includes(currentTitle, [PRIVACY_POLICY, ATTUNE_PRIVACY_POLICY]),
                always({
                    fileName: PRIVACY_POLICY_HBS,
                    additionalContent: {
                        pre_text: SiteSettings.generalPolicyPre(),
                        pre_text_link: SiteSettings.generalPolicyLink(),
                    },
                }),
            ],
            [
                equals(CONSENT),
                always({
                    fileName: CONSENT_HBS,
                    additionalContent: {
                        bank_name: SiteSettings.generalAppTitle(),
                        lender_contact_email: SiteSettings.lenderContactEmail(),
                        lender_phone_number: SiteSettings.lenderContactPhone(),
                        lender_address: SiteSettings.lenderContactAddress(),
                    },
                }),
            ],
            [equals(CARD_HOLDER_AGREEMENT), always({ fileName: CARD_HOLDER_AGREEMENT_HBS, additionalContent: {} })],
            [equals(PRICING_AND_TERMS), always({ fileName: PRICING_TERMS_HBS, additionalContent: {} })],
            [equals(RENASANT_BANK_PRIVACY_POLICY), always({ fileName: RENASANT_PRIVACY_HBS, additionalContent: {} })],
            [equals(RENASANT_BANK_TERMS_OF_USE), always({ fileName: RENASANT_TERMS_HBS, additionalContent: {} })],
            [equals(CONSENT_E_STATEMENTS), always({ fileName: E_STATEMENTS_TERMS_HBS, additionalContent: {} })],
            [equals(CONSENT_DIGITAL), always({ fileName: DIGITAL_TERMS_HBS, additionalContent: {} })],
            [equals(RSNT_CREDIT_POLICY), always({ fileName: CREDIT_CONSENT, additionalContent: {} })],
            [equals(STAR_CREDIT_POLICY), always({ fileName: STAR_CREDIT_CONSENT, additionalContent: {} })],
            [equals(COOKIEPOLICY), always({ fileName: COOKIE_POLICY_CONSENT, additionalContent: {} })],

            [T, always({ fileName: '', additionalContent: {} })],
        ])(currentTitle);
    };

    const loadHBSContent = (currentTitle: string, titleAlias?:string) => {
        const currentContent = getDefaultHBSOptions(currentTitle);
        const additionalContent = pathOr({}, ['additionalContent'], currentContent);
        const fileName = pathOr('', ['fileName'], currentContent);

        if (fileName === '') {
            console.error(`No fileName found for title: ${currentTitle}`);
            return;
        }

        fetch(getS3Url(fileName))
            .then((res) => res.text())
            .then((text) => {
                setContent(renderHandlebars(text, additionalContent));
                setTitle(titleAlias || currentTitle);
                setOpen(true);
            })
            .catch((error) => {
                console.error(`Error loading content for ${currentTitle}: `, error);
            });
    };

    const loadExternalURL = (policyOptions: any) => {
        const currentTitle = pathOr('', ['name'], policyOptions);
        setTitle(currentTitle);
        setExternalURL(pathOr('', ['iframe_external_url'], policyOptions));
        setOpenIframe(true);
    };

    const openExternalUrl = (url: string) => {
      // We certainly could manage the link using an href in the tsx element.
      // However, for consistency I am managing the external url here.
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    }

    const getContent = (policyOptions: TenantPolicyCondition) => {
        const currentTitle = policyOptions.name;
        const titleAlias = policyOptions.alias
        setContent('');

        let contentType;
        if (policyOptions.iframe_external_url) {
          contentType = CONTENT_EXTERNAL_URL;
        } else if (policyOptions.open_external_url) {
          contentType = CONTENT_OPEN_EXTERNAL_URL;
        } else {
          contentType = CONTENT_HBS;
        }

        const contentLoaderFn = cond([
            [equals(CONTENT_HBS), always(() => loadHBSContent(currentTitle, titleAlias))],
            [equals(CONTENT_EXTERNAL_URL), always(() => loadExternalURL(policyOptions))],
            [equals(CONTENT_OPEN_EXTERNAL_URL), always(() => openExternalUrl(policyOptions.open_external_url || ''))],
            [T, always(() => loadHBSContent(currentTitle))],
        ])(contentType);
        contentLoaderFn();
    };

    const handleOpen = (policyOptions: TenantPolicyCondition) => {
      getContent(policyOptions);
    };

    const escFunction = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleClose();
                handleIframeClose();
            }
        },
        [handleClose, handleIframeClose]
    );

    useEffect(() => {
        document.addEventListener('keydown', escFunction);
        return () => {
            document.removeEventListener('keydown', escFunction);
        };
    }, [escFunction]);

    return {
        open,
        openIframe,
        title,
        content,
        externalURL,
        handleOpen,
        handleClose,
        handleIframeClose,
    };
};
